import React from 'react';
import * as Yup from 'yup';
import { Formik, ErrorMessage } from 'formik';
import { Box, VStack, Heading, ScrollView, Text, Spacer } from 'native-base';

// Assets
import { ArrowRightIcon } from 'react-native-heroicons/solid';
import { theme } from '@breakroom-nx/assets';

// Components
import { PrimaryButton, TertiaryButton, TextField } from '../../';

// Redux
import { useAppDispatch, openModal, flipToPane } from '@breakroom-nx/redux';

// APIs
import {
  getAuth,
  RecaptchaVerifier,
  signInWithEmailAndPassword,
  browserLocalPersistence,
  setPersistence,
  Auth,
} from 'firebase/auth';

// import { analytics } from '@breakroom-nx/apis';
// import { logEvent } from 'firebase/analytics';

// Classes & Types
import { InputType, Status } from '@breakroom-nx/helpers';

//! Unsure if sanitizing input against malicious code such as '<' and '>' should be necessary even for password reset.
const LoginSchema = Yup.object().shape({
  email: Yup.string()
    .email('Invalid email')
    .required('Please enter your email'),
  password: Yup.string().required('Please enter your password'),
});

interface LoginFormValues {
  email: string;
  password: string;
}

/* eslint-disable-next-line */
export interface LoginProps {
  auth: Auth;
  onSuccess: () => void;
  display2FAValidation: (auth: Auth, error: any) => void;
}

export function Login({ auth, onSuccess, display2FAValidation }: LoginProps) {
  const initialValues: LoginFormValues = { email: '', password: '' };
  const dispatch = useAppDispatch();

  return (
    <ScrollView
      flex={1}
      maxHeight={'100%'}
      showsVerticalScrollIndicator={true}
      _contentContainerStyle={{
        pb: 8,
      }}
    >
      <VStack space={7} alignItems={'flex-start'}>
        <Heading mt={7} fontSize={30} lineHeight={34}>
          Login
        </Heading>
        <Formik
          initialValues={initialValues}
          validationSchema={LoginSchema}
          onSubmit={(values, { setSubmitting }) => {
            setPersistence(auth, browserLocalPersistence).then(
              (persistenceSet) => {
                signInWithEmailAndPassword(auth, values.email, values.password)
                  .then(async (credential) => {
                    setSubmitting(false);
                    onSuccess();
                    // logEvent(analytics, 'login');
                  })
                  .catch((error) => {
                    const errorCode = error.code;
                    let errorString = undefined;

                    if (error.code === 'auth/multi-factor-auth-required') {
                      display2FAValidation(auth, error);
                    } else {
                      setSubmitting(false);
                      switch (errorCode) {
                        case 'auth/user-not-found':
                        case 'auth/wrong-password':
                          errorString =
                            "Unsuccessful login, please review your credentials. If you forgot your password, please press 'Forgot?' to reset it.";
                          break;
                        default:
                          errorString =
                            'Unexpected error logging in, please try again.';
                          break;
                      }

                      dispatch(
                        openModal({
                          modalType: 'Alert',
                          modalProps: {
                            status: Status.error,
                            title: errorString,
                          },
                        })
                      );
                    }
                  });
              }
            );
          }}
        >
          {({
            values,
            isSubmitting,
            handleChange,
            handleBlur,
            handleSubmit,
          }) => (
            <>
              <VStack space={'3'} width={'full'}>
                <TextField
                  disabled={isSubmitting}
                  type={InputType.Email}
                  value={values.email}
                  onChangeText={handleChange('email')}
                  onBlur={handleBlur('email')}
                  onSubmitEditing={handleSubmit}
                  autoCompleteType={'email'}
                  autoFocus
                />
                <Text variant={'error'}>
                  <ErrorMessage name="email" />
                </Text>
                <TextField
                  disabled={isSubmitting}
                  type={InputType.Password}
                  value={values.password}
                  onChangeText={handleChange('password')}
                  onBlur={handleBlur('password')}
                  onSubmitEditing={handleSubmit}
                  autoCompleteType={'password'}
                />
                <Text variant={'error'}>
                  <ErrorMessage name="password" />
                </Text>

                <Box alignItems={'flex-end'}>
                  <TertiaryButton
                    disabled={isSubmitting}
                    onPress={() => {
                      dispatch(flipToPane('Forgot'));
                    }}
                    color={'primary.400'}
                    text={'Forgot?'}
                    fontSize={14}
                  />
                </Box>
              </VStack>
              <VStack space={3} width={'full'} mt={7}>
                <Spacer />
                <PrimaryButton
                  disabled={isSubmitting}
                  isLoading={isSubmitting}
                  text={'Login'}
                  onPress={handleSubmit}
                />
                <TertiaryButton
                  disabled={isSubmitting}
                  onPress={() => {
                    dispatch(flipToPane('Signup'));
                  }}
                  color={'primary.400'}
                  text={`Don't have an account? Sign up`}
                  endIcon={
                    <ArrowRightIcon
                      color={theme.colors.primary[400]}
                      size={15}
                    />
                  }
                />
              </VStack>
            </>
          )}
        </Formik>
      </VStack>
    </ScrollView>
  );
}

export default React.memo(Login);
